// src/pages/Settings/Settings.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../backend/supabaseClient';
import './Settings.css';

const Settings = ({ session, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'devices', 'account'
  
  // Profile settings
  const [profileSettings, setProfileSettings] = useState({
    username: currentUser?.username || '',
    email: session?.user?.email || ''
  });
  
  // Device settings
  const [deviceSettings, setDeviceSettings] = useState({
    leftFootDevice: '',
    rightFootDevice: ''
  });
  
  // Account settings
  const [accountSettings, setAccountSettings] = useState({
    newEmail: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user settings on mount
  const loadUserSettings = useCallback(async () => {
    if (!session?.user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('username, device_settings')
        .eq('id', session.user.id)
        .single();

      if (error) throw error;

      if (data) {
        setProfileSettings(prev => ({
          ...prev,
          username: data.username || ''
        }));
        
        setDeviceSettings({
          leftFootDevice: data.device_settings?.leftFootDevice || '',
          rightFootDevice: data.device_settings?.rightFootDevice || ''
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    loadUserSettings();
  }, [loadUserSettings]);

  // Handle profile update
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ username: profileSettings.username })
        .eq('id', session.user.id);

      if (error) throw error;

      setMessage({ 
        type: 'success', 
        text: 'Profile updated successfully!' 
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle device settings update
  const handleUpdateDevices = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          device_settings: {
            leftFootDevice: deviceSettings.leftFootDevice,
            rightFootDevice: deviceSettings.rightFootDevice
          }
        })
        .eq('id', session.user.id);

      if (error) throw error;

      setMessage({ 
        type: 'success', 
        text: 'Device settings saved successfully!' 
      });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle email update
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase.auth.updateUser({
        email: accountSettings.newEmail
      });

      if (error) throw error;

      setMessage({ 
        type: 'success', 
        text: 'Email update initiated. Please check your new email for confirmation.' 
      });
      
      setAccountSettings(prev => ({
        ...prev,
        newEmail: ''
      }));
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle password update
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (accountSettings.newPassword !== accountSettings.confirmPassword) {
      setMessage({ 
        type: 'error', 
        text: 'Passwords do not match' 
      });
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: accountSettings.newPassword
      });

      if (error) throw error;

      setMessage({ 
        type: 'success', 
        text: 'Password updated successfully!' 
      });
      
      setAccountSettings(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  const getDeviceDisplayName = (deviceSetting) => {
    if (!deviceSetting) return 'Not configured';
    
    // If it's just a number, show it as PSCore#
    if (/^\d+$/.test(deviceSetting)) {
      return `PSCore${deviceSetting}`;
    }
    
    return deviceSetting;
  };

  return (
    <div className="settings-container">
      <div className="settings-box">
        <h2>Settings</h2>
        
        {/* Tab Navigation */}
        <div className="settings-tabs">
          <button 
            className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button 
            className={`tab ${activeTab === 'devices' ? 'active' : ''}`}
            onClick={() => setActiveTab('devices')}
          >
            Devices
          </button>
          <button 
            className={`tab ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account
          </button>
        </div>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
        
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleUpdateProfile}>
            <div className="settings-section">
              <h3>Profile Information</h3>
              
              <div className="form-group">
                <label>Email (read-only)</label>
                <input
                  type="email"
                  value={profileSettings.email}
                  disabled
                  className="readonly"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  type="text"
                  value={profileSettings.username}
                  onChange={(e) => setProfileSettings(prev => ({
                    ...prev,
                    username: e.target.value
                  }))}
                  placeholder="Enter username"
                  pattern="[a-zA-Z0-9_-]{3,20}"
                  title="3-20 characters, letters, numbers, underscores, and hyphens only"
                />
              </div>
            </div>
            
            <button type="submit" disabled={loading} className="save-btn">
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        )}
        
        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <form onSubmit={handleUpdateDevices}>
            <div className="settings-section">
              <h3>Device Configuration</h3>
              
              <div className="current-devices">
                <h4>Currently Configured:</h4>
                <div className="device-status">
                  <div className="device-item">
                    <span className="device-label">Left Foot:</span>
                    <span className="device-value">
                      {getDeviceDisplayName(deviceSettings.leftFootDevice)}
                    </span>
                  </div>
                  <div className="device-item">
                    <span className="device-label">Right Foot:</span>
                    <span className="device-value">
                      {getDeviceDisplayName(deviceSettings.rightFootDevice)}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="settings-info">
                Enter the device names or numbers for your PointeSense sensors.
                For example: "PSCore1" or just "1"
              </p>
              
              <div className="form-group">
                <label htmlFor="leftFootDevice">Left Foot Device</label>
                <input
                  id="leftFootDevice"
                  type="text"
                  value={deviceSettings.leftFootDevice}
                  onChange={(e) => setDeviceSettings(prev => ({
                    ...prev,
                    leftFootDevice: e.target.value
                  }))}
                  placeholder="e.g., PSCore1 or 1"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="rightFootDevice">Right Foot Device</label>
                <input
                  id="rightFootDevice"
                  type="text"
                  value={deviceSettings.rightFootDevice}
                  onChange={(e) => setDeviceSettings(prev => ({
                    ...prev,
                    rightFootDevice: e.target.value
                  }))}
                  placeholder="e.g., PSCore2 or 2"
                />
              </div>
            </div>
            
            <button type="submit" disabled={loading} className="save-btn">
              {loading ? 'Saving...' : 'Save Device Settings'}
            </button>
          </form>
        )}
        
        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="account-settings">
            <div className="settings-section">
              <h3>Change Email</h3>
              <form onSubmit={handleUpdateEmail}>
                <div className="form-group">
                  <label htmlFor="newEmail">New Email</label>
                  <input
                    id="newEmail"
                    type="email"
                    value={accountSettings.newEmail}
                    onChange={(e) => setAccountSettings(prev => ({
                      ...prev,
                      newEmail: e.target.value
                    }))}
                    placeholder="Enter new email"
                    required
                  />
                </div>
                
                <button type="submit" disabled={loading} className="save-btn">
                  {loading ? 'Updating...' : 'Update Email'}
                </button>
              </form>
            </div>
            
            <div className="settings-section">
              <h3>Change Password</h3>
              <form onSubmit={handleUpdatePassword}>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={accountSettings.newPassword}
                    onChange={(e) => setAccountSettings(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    placeholder="Enter new password"
                    minLength="6"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={accountSettings.confirmPassword}
                    onChange={(e) => setAccountSettings(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    placeholder="Confirm new password"
                    minLength="6"
                    required
                  />
                </div>
                
                <button type="submit" disabled={loading} className="save-btn">
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;